import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import qs from 'qs'
import { useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import Button from '~components/ui/Button'
import Message from '~components/ui/Message'
import PaymentDialog from '~components/PaymentDialog'
import { pageView } from '~helpers/ga'
import { loadStripeForCurrency } from '~helpers/main'
import Prompt from '~components/ui/Prompt'
import PaymentForm from '~components/account/PaymentForm'
import Ribbon from '~components/Ribbon'
import { Elements } from '@stripe/react-stripe-js'
const stripeReturnUrl = process.env.MIX_STRIPE_RETURN_URL

const paymentFormTheme = {
  appearance: {
    variables: {
      colorPrimary: '#060707',
      colorBackground: '#ffffff',
      colorText: '#060707',
      colorDanger: '#df1b41',
      fontFamily: 'Avenir',
      spacingUnit: '6.25px',
      borderRadius: '6px'
    },
    rules: {
      '.Input': {
        borderColor: '#9b9b9b'
      },
      '.Input:focus': {
        borderColor: '#060707'
      }
    }
  }
}

const Payment = ({ account, isSuspended }) => {
  const location = useLocation()
  const stripePromise = loadStripeForCurrency(account.stockist.pricelist.currency)

  const paymentSuccessClientSecret = new URLSearchParams(window.location.search).get(
    'payment_intent_client_secret'
  )

  const [paymentSucceeded, setPaymentSucceeded] = useState(false)
  const [paymentDialog, setPaymentDialog] = useState(false)
  const [clientSecret, setClientSecret] = useState(paymentSuccessClientSecret)

  const [suspended, setSuspended] = useState(false)
  const [showSuspensionPrompt, setShowSuspensionPrompt] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  const [formData, setFormData] = useState({
    invoice_id: '',
    amount: '',
    first_name: account.first_name || '',
    last_name: account.last_name || '',
    address_1: account.stockist.address_1 || '',
    address_2: account.stockist.address_2 || '',
    city: account.stockist.address_4 || '',
    postcode: [account.stockist.postcode_1, account.stockist.postcode_2].join('')
  })

  useEffect(() => {
    pageView()
    setSuspensionPromptStatus()

    const query = qs.parse(location.search, { ignoreQueryPrefix: true })
    if (query.status !== undefined) {
      togglePayment()
    }
  }, [location])

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }))
  }

  const togglePayment = () => {
    setPaymentDialog(!paymentDialog)
    // Temporary until permanent solution found
    document.body.classList.toggle('overflow-y-hidden')
  }

  const getPaymentFormOptions = () => {
    return { ...paymentFormTheme, clientSecret }
  }

  const isPaymentFormReady = () => {
    return clientSecret && !busy && !paymentSucceeded
  }

  const setSuspensionPromptStatus = () => {
    setSuspended(isSuspended)
    setShowSuspensionPrompt(isSuspended)
  }

  const displaySuspensionPrompt = () => {
    const query = qs.parse(location.search, { ignoreQueryPrefix: true })
    return showSuspensionPrompt && query.status === '1'
  }

  const setMessagePrompt = ({ type, text }) => {
    setMessage({ type, text })

    setTimeout(() => {
      setMessage({ type: '' })
    }, 5000)
  }

  const submit = async (e) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)

    try {
      const { data } = await axios.post('/api/account/pay', formData)
      setClientSecret(data.client_secret)
    } catch (error) {
      setMessagePrompt({ type: 'error', text: 'All fields are required.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {message.text && (
        <Message
          messageType={message.type}
          messageText={message.text}
        />
      )}

      {paymentSucceeded && (
        <Ribbon
          status="success"
          text="Your payment has been successfully submitted"
          link="/account"
          linkText="Go to account"
        />
      )}

      {!paymentSucceeded && (
        <form
          className="mt-10 mb-10 xl:mt-0 xl:mb-20"
          onSubmit={submit}
        >
          <h3 className="pb-4 text-left border-b text-md border-coral">
            Payment
          </h3>

          <div className="w-full mt-3 lg:w-1/2 xl:w-1/3">
            <label className="inline-block mt-4">
              Payment Reference
            </label>

            <input
              name="invoice_id"
              type="text"
              required
              disabled={isPaymentFormReady()}
              value={formData.invoice_id}
              onChange={handleInputChange}
              className="relative block w-full p-4 mt-2 border rounded-md appearance-none placeholder-lightCharcoal text-charcoal border-lightCharcoal focus:border-charcoal focus:outline-none focus:z-10" placeholder="Payment Reference"
            />
          </div>

          <div className="w-full mt-3 lg:w-1/2 xl:w-1/3">
            <label className="inline-block mt-4">
              Amount
            </label>

            <input
              name="amount"
              type="number"
              required
              disabled={isPaymentFormReady()}
              value={formData.amount}
              onChange={handleInputChange}
              className="relative block w-full p-4 mt-2 border rounded-md appearance-none placeholder-lightCharcoal text-charcoal border-lightCharcoal focus:border-charcoal focus:outline-none focus:z-10" placeholder="Amount"
            />
          </div>

          <div className="mt-6">
            <Button
              type="submit"
              onClick={submit.bind(this)}
              disabled={busy}
              className="w-auto mt-4"
            >
              Proceed to payment
            </Button>
          </div>
        </form>
      )}

      {isPaymentFormReady() && (
        <section className="mt-10 mb-10 xl:mt-0 xl:mb-20">
          <h3 className="pb-4 text-left border-b text-md border-coral">
            Payment Details
          </h3>

          <div className="w-full mt-7 lg:w-1/2 xl:w-1/3">
            <Elements
              options={getPaymentFormOptions()}
              stripe={stripePromise}
            >
              <PaymentForm
                returnUrl={stripeReturnUrl}
                onSuccess={() => setPaymentSucceeded(true)}
                onError={(message) => setMessagePrompt({ type: 'error', text: message })}
              />
            </Elements>
          </div>
        </section>
      )}

      {paymentDialog && !isSuspended && (
        <PaymentDialog onClose={togglePayment} />
      )}

      {displaySuspensionPrompt() && (
        <Prompt
          title="Payment received"
          text="<p>Thank you for your payment, you will resume full access to B2B shortly. Please note: This may take a full working day to resume.</p>"
          onClose={() => setShowSuspensionPrompt(false)}
          onConfirm={() => setShowSuspensionPrompt(false)}
        />
      )}
    </>
  )
}

Payment.propTypes = {
  account: PropTypes.object,
  isSuspended: PropTypes.bool
}

const mapStateToProps = state => {
  return {
    account: state.auth.account,
    stockist: state.auth.account.stockist,
    isSuspended: state.auth.isSuspended
  }
}

export default connect(mapStateToProps)(Payment)
